import React, { useEffect, useRef, useState } from "react";

import { Alert, Checkbox, Field, NumberInput, Select, StatePanel, Toggle, useToast } from "../../components/ui";
import { waBroadcastApi, apiError } from "../../waBroadcast/waBroadcastApi";
import { DAY_NAMES, dayRangeLabel, pad } from "../../waBroadcast/waBroadcastHelpers";

import "../../waBroadcast/wa-broadcast.scss";

const HOURS = Array.from({ length: 25 }, (_, h) => ({ value: h, label: `${pad(h)}:00` }));

/**
 * Retail Circle Admin → WhatsApp Broadcast: the rules every store sends by
 * (Alex, 2026-10-13). Each store's "Daily limit" card shows these live.
 *  - sending window: hours, days, time zone — or each store's own time zone
 *    (set in Store Settings), falling back to this one;
 *  - chats per number per hour;
 *  - posts per group per day: a number, or no limit;
 *  - the repeat rule: on/off, and its number of days.
 * Saves the moment a value changes — no Save button.
 */
export function WaRulesPanel() {
  const toast = useToast();
  const [rules, setRules] = useState(null);
  const [zones, setZones] = useState([]);
  const [save, setSave] = useState("idle");
  const [error, setError] = useState("");
  const savedTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    waBroadcastApi
      .rules()
      .then((res) => {
        if (!alive) return;
        setRules(res.rules);
        setZones((res.timezones || []).map((z) => ({ value: z, label: z.replace(/_/g, " ") })));
      })
      .catch((err) => alive && setError(apiError(err, "Could not load the WhatsApp Broadcast rules.")));
    return () => {
      alive = false;
      clearTimeout(savedTimer.current);
    };
  }, []);

  const persist = async (patch, message) => {
    const previous = rules;
    setRules({ ...rules, ...patch });
    setSave("saving");
    setError("");
    try {
      setRules((await waBroadcastApi.setRules(patch)).rules);
      setSave("saved");
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSave("idle"), 2500);
      if (message) toast.success(message, { description: "Every store's Daily limit card shows the new value." });
    } catch (err) {
      setRules(previous);
      setSave("idle");
      setError(apiError(err, "Couldn't save — try again."));
    }
  };

  if (!rules) {
    return error ? <Alert tone="error" title={error} /> : <StatePanel state="loading" loadingShape="row" compact />;
  }

  const toggleDay = (day, on) => {
    const days = on ? [...new Set([...rules.days, day])].sort() : rules.days.filter((d) => d !== day);
    if (!days.length) {
      setError("Keep at least one sending day.");
      return;
    }
    persist({ days }, `Sending days: ${dayRangeLabel(days)}`);
  };

  return (
    <div className="wa-numbers-panel wa-rules">
      <section className="wa-numbers-panel__block">
        <h4 className="wa-numbers-panel__title rc-wab-heading">Sending hours</h4>
        <div className="wa-rules__row">
          <Field label="From">
            <Select
              label="From"
              options={HOURS.slice(0, 24)}
              value={rules.start_hour}
              onChange={(v) => v != null && persist({ start_hour: v }, `Sending from ${pad(v)}:00`)}
            />
          </Field>
          <Field label="Until">
            <Select
              label="Until"
              options={HOURS.slice(1)}
              value={rules.end_hour}
              onChange={(v) => v != null && persist({ end_hour: v }, `Sending until ${pad(v)}:00`)}
            />
          </Field>
        </div>
        <div className="wa-rules__days" role="group" aria-label="Sending days">
          {DAY_NAMES.map((name, i) => (
            <Checkbox key={name} checked={rules.days.includes(i + 1)} onChange={(on) => toggleDay(i + 1, on)} label={name} />
          ))}
        </div>
        <Field label="Time zone">
          <Select
            label="Time zone"
            searchable
            options={zones}
            value={rules.timezone}
            onChange={(v) => v && persist({ timezone: v }, `Time zone: ${v.replace(/_/g, " ")}`)}
          />
        </Field>
        <Toggle
          size="md"
          checked={!!rules.use_store_timezone}
          onChange={(next) =>
            persist({ use_store_timezone: next }, next ? "Stores use their own time zone" : "Every store uses this time zone")
          }
          label="Use each store's own time zone when it has set one"
        />
        <p className="wa-numbers-panel__note">
          A store sets its time zone in Settings → Store Settings. Stores without one use the time zone above.
        </p>
      </section>

      <section className="wa-numbers-panel__block">
        <h4 className="wa-numbers-panel__title rc-wab-heading">Chats per hour</h4>
        <Field label="At most, per number, per hour">
          <NumberInput
            value={rules.hourly_cap}
            min={1}
            max={100}
            stepper
            size="md"
            aria-label="Chats per number per hour"
            onChange={(v) => v && v !== rules.hourly_cap && persist({ hourly_cap: v }, `At most ${v} chats an hour per number`)}
          />
        </Field>
        <p className="wa-numbers-panel__note">More chats an hour means more risk of a WhatsApp ban.</p>
      </section>

      <section className="wa-numbers-panel__block">
        <h4 className="wa-numbers-panel__title rc-wab-heading">Posts per group per day</h4>
        <Toggle
          size="md"
          checked={rules.per_group_daily == null}
          onChange={(none) => persist({ per_group_daily: none ? null : 20 }, none ? "No limit per group" : "Limit per group: 20 a day")}
          label="No limit"
        />
        {rules.per_group_daily != null && (
          <Field label="At most, per group, per day">
            <NumberInput
              value={rules.per_group_daily}
              min={1}
              max={500}
              stepper
              size="md"
              aria-label="Posts per group per day"
              onChange={(v) => v && v !== rules.per_group_daily && persist({ per_group_daily: v }, `At most ${v} posts per group per day`)}
            />
          </Field>
        )}
      </section>

      <section className="wa-numbers-panel__block">
        <h4 className="wa-numbers-panel__title rc-wab-heading">Repeat rule</h4>
        <Toggle
          size="md"
          checked={!!rules.repeat_enabled}
          onChange={(on) => persist({ repeat_enabled: on }, on ? "Repeat rule on" : "Repeat rule off")}
          label="Never send the same item to the same group twice within a set number of days"
        />
        {rules.repeat_enabled && (
          <Field label="Days">
            <NumberInput
              value={rules.repeat_days}
              min={1}
              max={90}
              stepper
              size="md"
              aria-label="Repeat rule days"
              onChange={(v) => v && v !== rules.repeat_days && persist({ repeat_days: v }, `Same item at most once per group every ${v} days`)}
            />
          </Field>
        )}
      </section>

      <p className="wa-numbers-panel__note" aria-live="polite">
        {save === "saving" ? "Saving…" : save === "saved" ? "All changes saved ✓" : ""}
      </p>
      {error && <Alert tone="error" title={error} />}
    </div>
  );
}

export default WaRulesPanel;
